import React, { useState } from 'react';
import { formatTenantAddress } from '@/utils/tenantDisplay';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { SearchInput } from '@/components/UI/SearchInput';
import { StatCard } from '@/components/UI/StatCard';
import { Chip } from '@/components/UI/Chip';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import {
  useListPlatformTenantsQuery,
  PlatformTenant,
} from '@/redux/internal-services/platformAdminApi';
import CloneTenantModal from './CloneTenantModal';

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  Active: 'success',
  Trial: 'accent',
  Suspended: 'warning',
  Deactivated: 'danger',
};

const STATUSES = ['All', 'Active', 'Trial', 'Suspended', 'Deactivated'];

const TenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [cloneTenant, setCloneTenant] = useState<PlatformTenant | null>(null);

  const { data, isLoading, isFetching } = useListPlatformTenantsQuery(
    { search: search || undefined, status: status || undefined },
    { refetchOnMountOrArgChange: true }
  );

  const tenants: PlatformTenant[] = data?.data ?? [];
  const total = data?.total ?? 0;

  const activeCount   = tenants.filter((t) => t.lifecycle_status === 'Active').length;
  const trialCount    = tenants.filter((t) => t.lifecycle_status === 'Trial').length;
  const suspCount     = tenants.filter((t) => t.lifecycle_status === 'Suspended').length;

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="Tenant Management"
          subtitle="View and manage all tenants across the platform."
          toolbar={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants..."
              />
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <Chip
                    key={s}
                    selected={s === 'All' ? status === '' : status === s}
                    onClick={() => setStatus(s === 'All' ? '' : s)}
                  >
                    {s}
                  </Chip>
                ))}
              </div>
            </div>
          }
        />
      }
    >

      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Tenants" value={isLoading ? '—' : total} tone="accent" />
        <StatCard label="Active" value={isLoading ? '—' : activeCount} tone="success" />
        <StatCard label="Trial" value={isLoading ? '—' : trialCount} tone="accent" />
        <StatCard label="Suspended" value={isLoading ? '—' : suspCount} tone="warning" />
      </div>

      {/* Table */}
      <div className="table-card">
        {isLoading || isFetching ? (
          <LoadingState label="Loading tenants..." />
        ) : tenants.length === 0 ? (
          <EmptyState title={t('platform.noTenantsFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th-cell">Company</th>
                  <th className="th-cell">Status</th>
                  <th className="th-cell hidden md:table-cell">Plan</th>
                  <th className="th-cell">Users</th>
                  <th className="th-cell hidden lg:table-cell">Created</th>
                  <th className="th-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr
                    key={tenant.tenant_id}
                    className="tr-hover"
                    onClick={() => navigate(`/platform/tenants/${tenant.tenant_id}`)}
                  >
                    <td className="td-cell">
                      <p className="font-medium">{tenant.company_name}</p>
                      {tenant.primary_contact?.email && (
                        <p className="mt-0.5 text-xs text-ui-text3">{tenant.primary_contact.email}</p>
                      )}
                      {formatTenantAddress(tenant.address) && (
                        <p className="mt-0.5 text-xs text-ui-text3">{formatTenantAddress(tenant.address)}</p>
                      )}
                      <p className="mt-0.5 break-all font-mono text-micro text-ui-text3">{tenant.tenant_id}</p>
                    </td>
                    <td className="td-cell">
                      <Badge variant={STATUS_VARIANT[tenant.lifecycle_status] ?? 'neutral'}>
                        {tenant.lifecycle_status}
                      </Badge>
                    </td>
                    <td className="td-cell hidden text-ui-text2 md:table-cell">{tenant.plan_name ?? '—'}</td>
                    <td className="td-cell text-ui-text2">{tenant.user_count ?? '—'}</td>
                    <td className="td-cell hidden text-ui-text3 lg:table-cell">
                      {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="td-cell text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={(e) => { e.stopPropagation(); setCloneTenant(tenant); }}
                        >
                          Clone
                        </Button>
                        <Button
                          variant="link"
                          size="xs"
                          className="text-accent-text"
                          onClick={(e) => { e.stopPropagation(); navigate(`/platform/tenants/${tenant.tenant_id}`); }}
                        >
                          Manage →
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {cloneTenant && (
        <CloneTenantModal
          sourceTenant={cloneTenant}
          onClose={() => setCloneTenant(null)}
        />
      )}
    </PageShell>
  );
};

export default TenantsPage;
